import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, PercentIcon, InfoIcon, TagIcon, TagsIcon, TicketIcon, BanknoteIcon, RefreshCwIcon, CreditCardIcon, GlobeIcon, HashIcon, LayersIcon, SearchIcon, PlusIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
import { FormField } from '../components/FormField';
import { SearchableSelect } from '../components/SearchableSelect';
// Mock campaigns data - would be replaced with API calls in a real app
const mockCampaigns: Record<string, any> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    startDate: '01/10/2025',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 5000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'Mobile Banking'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active'
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (THB 10 Discount)',
    description: 'DISCOUNT_COUPON',
    startDate: '01/08/2025',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 11000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card'],
    cardTargetingMode: 'bins',
    cardBrands: ['Visa', 'Mastercard'],
    cardBin: '411111',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  },
  BULK2025: {
    id: 'BULK2025',
    title: 'Summer Festival Bulk Codes',
    description: 'Unique codes for summer festival attendees',
    startDate: '01/06/2025',
    expiryDate: '31/08/2025',
    discountPercentage: 15,
    fixedDiscountAmount: 0,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 50000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'PromptPay'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: '',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Bulk',
    minTransactionValue: 500,
    maxTransactionValue: 999999,
    minDiscount: 0,
    maxDiscount: 1000,
    status: 'active'
  }
};
// Empty campaign template for new campaigns
const emptyCampaign: any = {
  id: '',
  title: '',
  description: '',
  startDate: '',
  expiryDate: '',
  discountPercentage: 0,
  fixedDiscountAmount: 0,
  currency: 'THB',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 0,
  recurringBudget: 0,
  recurringBudgetPeriod: 'monthly',
  strictlyEnforceBudget: true,
  paymentMethods: [],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardIssuingCountries: [],
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 0,
  status: 'active'
};
const RECURRING_PERIODS = ['Daily', 'Weekly', 'Monthly'];
const CARD_TYPES = ['Credit', 'Debit', 'Prepaid'];
const CARD_CATEGORIES = ['Consumer', 'Commercial', 'Corporate', 'Premium', 'Platinum', 'Gold', 'Classic'];
const CARD_RULE_DEFINITIONS = [{
  id: 'brands',
  label: 'Card Brands',
  icon: CreditCardIcon,
  description: 'Filter by Visa, Mastercard, etc.'
}, {
  id: 'types',
  label: 'Card Type',
  icon: LayersIcon,
  description: 'Filter by Credit, Debit, Prepaid'
}, {
  id: 'bins',
  label: 'Card BIN',
  icon: HashIcon,
  description: 'Filter by Bank Identification Numbers'
}, {
  id: 'countries',
  label: 'Issuing Country',
  icon: GlobeIcon,
  description: 'Filter by card issuing country'
}, {
  id: 'categories',
  label: 'Card Category',
  icon: CreditCardIcon,
  description: 'Filter by Consumer, Commercial, etc.'
}] as const;
const PAYMENT_METHODS_LIST = ['Card', 'Mobile Banking', 'LINE Pay', 'Rabbit Card', 'WeChat Pay', 'Alipay', 'TrueMoney Wallet', 'PromptPay', 'QR Payment', 'Installment', 'Internet Banking'];
const CARD_BRANDS = ['Visa', 'Mastercard', 'UnionPay', 'American Express', 'JCB', 'TPN', 'Discover', 'Diners Club'];
const COUNTRIES = [{
  code: 'TH',
  name: 'Thailand'
}, {
  code: 'US',
  name: 'United States'
}, {
  code: 'GB',
  name: 'United Kingdom'
}, {
  code: 'SG',
  name: 'Singapore'
}, {
  code: 'JP',
  name: 'Japan'
}, {
  code: 'CN',
  name: 'China'
}, {
  code: 'KR',
  name: 'South Korea'
}, {
  code: 'MY',
  name: 'Malaysia'
}, {
  code: 'ID',
  name: 'Indonesia'
}, {
  code: 'PH',
  name: 'Philippines'
}, {
  code: 'VN',
  name: 'Vietnam'
}, {
  code: 'IN',
  name: 'India'
}, {
  code: 'AU',
  name: 'Australia'
}, {
  code: 'NZ',
  name: 'New Zealand'
}, {
  code: 'HK',
  name: 'Hong Kong'
}, {
  code: 'TW',
  name: 'Taiwan'
}, {
  code: 'DE',
  name: 'Germany'
}, {
  code: 'FR',
  name: 'France'
}, {
  code: 'IT',
  name: 'Italy'
}, {
  code: 'ES',
  name: 'Spain'
}, {
  code: 'NL',
  name: 'Netherlands'
}, {
  code: 'CH',
  name: 'Switzerland'
}, {
  code: 'SE',
  name: 'Sweden'
}, {
  code: 'CA',
  name: 'Canada'
}, {
  code: 'BR',
  name: 'Brazil'
}, {
  code: 'MX',
  name: 'Mexico'
}, {
  code: 'AE',
  name: 'United Arab Emirates'
}, {
  code: 'SA',
  name: 'Saudi Arabia'
}, {
  code: 'RU',
  name: 'Russia'
}, {
  code: 'ZA',
  name: 'South Africa'
}, {
  code: 'KH',
  name: 'Cambodia'
}, {
  code: 'LA',
  name: 'Laos'
}, {
  code: 'MM',
  name: 'Myanmar'
}, {
  code: 'BD',
  name: 'Bangladesh'
}, {
  code: 'LK',
  name: 'Sri Lanka'
}];
interface ChoiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}
function ChoiceCard({
  title,
  description,
  icon,
  selected,
  onClick
}: ChoiceCardProps) {
  return <div onClick={onClick} className={`cursor-pointer rounded-lg border p-4 transition-all duration-200 flex items-start gap-3 ${selected ? 'border-[#F7941D] bg-[#FFF8F1] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-6428">
      <div className="flex-shrink-0 mt-0.5" data-id="element-6429">
        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-[#F7941D] bg-[#F7941D]' : 'border-gray-300 bg-white'}`} data-id="element-6430">
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-6431" />}
        </div>
      </div>
      <div className="flex-1" data-id="element-6432">
        <div className="flex items-center gap-2 mb-1" data-id="element-6433">
          <span className={`flex-shrink-0 ${selected ? 'text-[#F7941D]' : 'text-gray-500'}`} data-id="element-6434">
            {icon}
          </span>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-6435">{title}</h3>
        </div>
        <p className="text-sm text-gray-500" data-id="element-6436">{description}</p>
      </div>
    </div>;
}
interface CountryMultiSelectProps {
  selectedCountries: Record<string, boolean>;
  onToggle: (code: string) => void;
  countrySearch: string;
  onSearchChange: (val: string) => void;
  filteredCountries: {
    code: string;
    name: string;
  }[];
  selectedCountryList: {
    code: string;
    name: string;
  }[];
}
function CountryMultiSelect({
  selectedCountries,
  onToggle,
  countrySearch,
  onSearchChange,
  filteredCountries,
  selectedCountryList
}: CountryMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative" data-id="element-6437">
      {/* Trigger / Display */}
      <div onClick={() => setIsOpen(!isOpen)} className="w-full min-h-[42px] px-3 py-2 border border-gray-200 rounded-lg text-sm cursor-pointer flex flex-wrap items-center gap-1.5 bg-white hover:border-gray-300 transition-colors" data-id="element-6438">
        {selectedCountryList.length > 0 ? <>
            {selectedCountryList.map(c => <span key={c.code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-50 border border-[#F7941D]/30 text-[#92400E] text-xs font-medium" data-id="element-6439">
                {c.code}
                <button type="button" onClick={e => {
            e.stopPropagation();
            onToggle(c.code);
          }} className="text-[#92400E]/60 hover:text-red-700 ml-0.5 leading-none" data-id="element-6440">
                  ×
                </button>
              </span>)}
          </> : <span className="text-gray-400" data-id="element-6441">Select countries...</span>}
      </div>

      {/* Dropdown */}
      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => {
        setIsOpen(false);
        onSearchChange('');
      }} data-id="element-6442" />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-6443">
            <div className="p-2 border-b border-gray-100" data-id="element-6444">
              <div className="relative" data-id="element-6445">
                <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-6446" />
                <input type="text" value={countrySearch} onChange={e => onSearchChange(e.target.value)} placeholder="Search countries..." className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D] focus:outline-none" autoFocus onClick={e => e.stopPropagation()} data-id="element-6447" />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto" data-id="element-6448">
              {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} onClick={e => {
            e.stopPropagation();
            onToggle(country.code);
          }} className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm transition-colors ${selectedCountries[country.code] ? 'bg-orange-50 text-[#92400E]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-6449">
                    <span data-id="element-6450">
                      <span className="font-medium" data-id="element-6451">{country.code}</span>
                      <span className="ml-2 text-gray-500" data-id="element-6452">{country.name}</span>
                    </span>
                    {selectedCountries[country.code] && <span className="text-[#F7941D] text-xs font-bold" data-id="element-6453">
                        ✓
                      </span>}
                  </div>) : <div className="px-3 py-4 text-sm text-gray-400 text-center" data-id="element-6454">
                  No countries found.
                </div>}
            </div>
          </div>
        </>}
    </div>;
}
export function CampaignForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaign, setCampaign] = useState<any>(emptyCampaign);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [budgetType, setBudgetType] = useState<'fixed' | 'recurring'>('fixed');
  const [activeCardRules, setActiveCardRules] = useState<string[]>([]);
  const [showAddRuleMenu, setShowAddRuleMenu] = useState(false);
  const [selectedCardTypes, setSelectedCardTypes] = useState<Record<string, boolean>>({});
  const [selectedCardCategories, setSelectedCardCategories] = useState<Record<string, boolean>>({});
  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({
    Sunday: true,
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true
  });
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<Record<string, boolean>>({});
  const [selectedCardBrands, setSelectedCardBrands] = useState<Record<string, boolean>>(CARD_BRANDS.reduce((acc, brand) => {
    acc[brand] = true;
    return acc;
  }, {} as Record<string, boolean>));
  const [selectedCountries, setSelectedCountries] = useState<Record<string, boolean>>({});
  const [countrySearch, setCountrySearch] = useState('');
  useEffect(() => {
    const campaignId = searchParams.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      const loadedCampaign = mockCampaigns[campaignId];
      setCampaign(loadedCampaign);
      setIsEditing(true);
      setDiscountType(loadedCampaign.discountPercentage > 0 ? 'percentage' : 'fixed');
      setBudgetType(loadedCampaign.budgetType || 'fixed');
      // Load active card rules from campaign data
      const rules: string[] = [];
      if (loadedCampaign.cardBrands?.length > 0) rules.push('brands');
      if (loadedCampaign.cardType && loadedCampaign.cardType !== 'All Types') rules.push('types');
      if (loadedCampaign.cardBin) rules.push('bins');
      if (loadedCampaign.cardIssuingCountries?.length > 0) rules.push('countries');
      if (loadedCampaign.cardCategories?.length > 0) rules.push('categories');
      setActiveCardRules(rules);
      // Set selected days based on campaign data
      const days = loadedCampaign.validDays.reduce((acc: Record<string, boolean>, day: string) => {
        acc[day] = true;
        return acc;
      }, {});
      setSelectedDays(days);
      // Set selected payment methods
      const methods = (loadedCampaign.paymentMethods || []).reduce((acc: Record<string, boolean>, method: string) => {
        acc[method] = true;
        return acc;
      }, {});
      setSelectedPaymentMethods(methods);
      // Set selected card brands
      const brands = (loadedCampaign.cardBrands || []).reduce((acc: Record<string, boolean>, brand: string) => {
        acc[brand] = true;
        return acc;
      }, {});
      setSelectedCardBrands(brands);
      // Set selected countries
      const countries = (loadedCampaign.cardIssuingCountries || []).reduce((acc: Record<string, boolean>, code: string) => {
        acc[code] = true;
        return acc;
      }, {});
      setSelectedCountries(countries);
    }
    setLoading(false);
  }, [searchParams]);
  const navigateBack = () => {
    if (isEditing) {
      navigate(`/campaign-details?id=${campaign.id}`);
    } else {
      navigate('/campaigns');
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setCampaign({
        ...campaign,
        [name]: checkbox.checked
      });
      return;
    }
    if (type === 'number') {
      setCampaign({
        ...campaign,
        [name]: parseFloat(value) || 0
      });
      return;
    }
    setCampaign({
      ...campaign,
      [name]: value
    });
  };
  const handleDayToggle = (day: string) => {
    const newSelectedDays = {
      ...selectedDays,
      [day]: !selectedDays[day]
    };
    setSelectedDays(newSelectedDays);
    const validDays = Object.entries(newSelectedDays).filter(([_, isSelected]) => isSelected).map(([day]) => day);
    setCampaign({
      ...campaign,
      validDays
    });
  };
  const handlePaymentMethodToggle = (method: string) => {
    const newSelectedMethods = {
      ...selectedPaymentMethods,
      [method]: !selectedPaymentMethods[method]
    };
    setSelectedPaymentMethods(newSelectedMethods);
    const paymentMethods = Object.entries(newSelectedMethods).filter(([_, isSelected]) => isSelected).map(([method]) => method);
    setCampaign({
      ...campaign,
      paymentMethods
    });
  };
  const handleCardBrandToggle = (brand: string) => {
    const newSelectedBrands = {
      ...selectedCardBrands,
      [brand]: !selectedCardBrands[brand]
    };
    setSelectedCardBrands(newSelectedBrands);
    const cardBrands = Object.entries(newSelectedBrands).filter(([_, isSelected]) => isSelected).map(([brand]) => brand);
    setCampaign({
      ...campaign,
      cardBrands
    });
  };
  const handleCountryToggle = (code: string) => {
    const newSelectedCountries = {
      ...selectedCountries,
      [code]: !selectedCountries[code]
    };
    setSelectedCountries(newSelectedCountries);
    const cardIssuingCountries = Object.entries(newSelectedCountries).filter(([_, isSelected]) => isSelected).map(([code]) => code);
    setCampaign({
      ...campaign,
      cardIssuingCountries
    });
  };
  const handleCardTypeToggle = (type: string) => {
    const newSelected = {
      ...selectedCardTypes,
      [type]: !selectedCardTypes[type]
    };
    setSelectedCardTypes(newSelected);
    const cardTypes = Object.entries(newSelected).filter(([_, v]) => v).map(([k]) => k);
    setCampaign({
      ...campaign,
      cardTypes
    });
  };
  const handleCardCategoryToggle = (cat: string) => {
    const newSelected = {
      ...selectedCardCategories,
      [cat]: !selectedCardCategories[cat]
    };
    setSelectedCardCategories(newSelected);
    const cardCategories = Object.entries(newSelected).filter(([_, v]) => v).map(([k]) => k);
    setCampaign({
      ...campaign,
      cardCategories
    });
  };
  const addCardRule = (ruleId: string) => {
    if (!activeCardRules.includes(ruleId)) {
      setActiveCardRules([...activeCardRules, ruleId]);
    }
    setShowAddRuleMenu(false);
  };
  const removeCardRule = (ruleId: string) => {
    setActiveCardRules(activeCardRules.filter(r => r !== ruleId));
    // Clear the rule's data
    if (ruleId === 'brands') {
      setSelectedCardBrands({});
      setCampaign({
        ...campaign,
        cardBrands: []
      });
    } else if (ruleId === 'types') {
      setSelectedCardTypes({});
      setCampaign({
        ...campaign,
        cardTypes: []
      });
    } else if (ruleId === 'bins') {
      setCampaign({
        ...campaign,
        cardBin: ''
      });
    } else if (ruleId === 'countries') {
      setSelectedCountries({});
      setCampaign({
        ...campaign,
        cardIssuingCountries: []
      });
    } else if (ruleId === 'categories') {
      setSelectedCardCategories({});
      setCampaign({
        ...campaign,
        cardCategories: []
      });
    }
  };
  const availableRules = CARD_RULE_DEFINITIONS.filter(r => !activeCardRules.includes(r.id));
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountryList = COUNTRIES.filter(c => selectedCountries[c.code]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting campaign:', {
      ...campaign,
      activeCardRules
    });
    navigate('/campaigns');
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-6455">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7941D]" data-id="element-6456"></div>
      </div>;
  }
  const inputClasses = 'w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] focus:outline-none transition-colors';
  const amountInputClasses = 'w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] focus:outline-none transition-colors';
  return <div className="max-w-4xl mx-auto space-y-6 pb-12" data-id="element-6457">
      {/* Header */}
      <div data-id="element-6458">
        <button onClick={navigateBack} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold mb-4" data-id="element-6459">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-6460" />
          Back to Campaigns
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-6461">
          {isEditing ? 'Update Campaign' : 'Create Campaign'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6462">
        {/* Section 1: Campaign Details */}
        <div className="p-6 border-b border-gray-100" data-id="element-6463">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6464">
            Campaign Details
          </h2>
          <div className="space-y-6" data-id="element-6465">
            <FormField label="Title" required data-id="element-6466">
              <input type="text" name="title" value={campaign.title} onChange={handleInputChange} required placeholder="e.g. Summer Sale 2025" className={inputClasses} data-id="element-6467" />
            </FormField>

            <FormField label="Description" data-id="element-6468">
              <textarea name="description" value={campaign.description} onChange={handleInputChange} rows={3} placeholder="Brief description of the campaign..." className={`${inputClasses} resize-none`} data-id="element-6469" />
            </FormField>
          </div>
        </div>

        {/* Section 2: Code Strategy */}
        <div className="p-6 border-b border-gray-100" data-id="element-6470">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6471">
            Campaign Code
          </h2>
          <div className="space-y-6" data-id="element-6472">
            <FormField label="Code Strategy" required data-id="element-6473">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6474">
                <ChoiceCard title="Single Global Code" description="One code that can be used by multiple customers (e.g. SUMMER25)" icon={<TagIcon size={18} data-id="element-6476" />} selected={campaign.codeStrategy === 'Global'} onClick={() => setCampaign({
                ...campaign,
                codeStrategy: 'Global'
              })} data-id="element-6475" />
                <ChoiceCard title="Unique Bulk Codes" description="System generates multiple unique codes for single use" icon={<TagsIcon size={18} data-id="element-6478" />} selected={campaign.codeStrategy === 'Bulk'} onClick={() => setCampaign({
                ...campaign,
                codeStrategy: 'Bulk'
              })} data-id="element-6477" />
              </div>
            </FormField>

            {campaign.codeStrategy === 'Global' && <FormField label="Global Campaign Code" required data-id="element-6479">
                <input type="text" name="code" value={campaign.code} onChange={handleInputChange} required placeholder="e.g. SUMMER25" className={inputClasses} data-id="element-6480" />
              </FormField>}
          </div>
        </div>

        {/* Section 3: Discount Configuration */}
        <div className="p-6 border-b border-gray-100" data-id="element-6481">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6482">
            Discount Configuration
          </h2>
          <div className="space-y-6" data-id="element-6483">
            <FormField label="Discount Type" required data-id="element-6484">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6485">
                <ChoiceCard title="Fixed Amount" description="A specific THB amount off the total purchase" icon={<BanknoteIcon size={18} data-id="element-6487" />} selected={discountType === 'fixed'} onClick={() => {
                setDiscountType('fixed');
                setCampaign({
                  ...campaign,
                  discountPercentage: 0
                });
              }} data-id="element-6486" />
                <ChoiceCard title="Percentage" description="A percentage off the total purchase" icon={<PercentIcon size={18} data-id="element-6489" />} selected={discountType === 'percentage'} onClick={() => {
                setDiscountType('percentage');
                setCampaign({
                  ...campaign,
                  fixedDiscountAmount: 0
                });
              }} data-id="element-6488" />
              </div>
            </FormField>

            <div className="max-w-md" data-id="element-6490">
              {discountType === 'fixed' ? <FormField label="Fixed Discount Amount" required data-id="element-6491">
                  <div className="relative" data-id="element-6492">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6493">
                      <span className="text-gray-500 font-medium text-sm" data-id="element-6494">
                        THB
                      </span>
                    </div>
                    <input type="number" name="fixedDiscountAmount" value={campaign.fixedDiscountAmount || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6495" />
                  </div>
                </FormField> : <FormField label="Discount Percentage" required data-id="element-6496">
                  <div className="relative" data-id="element-6497">
                    <input type="number" name="discountPercentage" value={campaign.discountPercentage || ''} onChange={handleInputChange} min="0" max="100" placeholder="0" className={inputClasses} data-id="element-6498" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-6499">
                      <PercentIcon size={18} className="text-gray-400" data-id="element-6500" />
                    </div>
                  </div>
                </FormField>}
            </div>
          </div>
        </div>

        {/* Section 4: Payment Methods */}
        <div className="p-6 border-b border-gray-100" data-id="element-6501">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6502">
            Payment Methods
          </h2>
          <div className="space-y-6" data-id="element-6503">
            <FormField label="Valid for which payment methods" data-id="element-6504">
              <p className="text-xs text-gray-500 mb-3" data-id="element-6505">
                Select the payment methods this campaign applies to. If none are
                selected, it applies to all.
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-6506">
                {PAYMENT_METHODS_LIST.map(method => <label key={method} className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${selectedPaymentMethods[method] ? 'bg-orange-50 border-[#F7941D] text-[#92400E]' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-6507">
                    <input type="checkbox" className="sr-only" checked={selectedPaymentMethods[method] || false} onChange={() => handlePaymentMethodToggle(method)} data-id="element-6508" />
                    <span className="text-sm font-medium" data-id="element-6509">{method}</span>
                  </label>)}
              </div>
            </FormField>
          </div>
        </div>

        {/* Section 5: Budget */}
        <div className="p-6 border-b border-gray-100" data-id="element-6510">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6511">
            Budget
          </h2>
          <div className="space-y-6" data-id="element-6512">
            <FormField label="Budget Type" required data-id="element-6513">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6514">
                <ChoiceCard title="Fixed Budget" description="Set a one-time total budget for the campaign" icon={<BanknoteIcon size={18} data-id="element-6516" />} selected={budgetType === 'fixed'} onClick={() => {
                setBudgetType('fixed');
                setCampaign({
                  ...campaign,
                  budgetType: 'fixed',
                  recurringBudget: 0
                });
              }} data-id="element-6515" />
                <ChoiceCard title="Recurring Budget" description="Budget resets automatically each period until expiry" icon={<RefreshCwIcon size={18} data-id="element-6518" />} selected={budgetType === 'recurring'} onClick={() => {
                setBudgetType('recurring');
                setCampaign({
                  ...campaign,
                  budgetType: 'recurring',
                  budget: 0
                });
              }} data-id="element-6517" />
              </div>
            </FormField>

            <div className="max-w-xl" data-id="element-6519">
              {budgetType === 'fixed' ? <FormField label="Total Budget" required data-id="element-6520">
                  <div className="relative" data-id="element-6521">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6522">
                      <span className="text-gray-500 font-medium text-sm" data-id="element-6523">
                        THB
                      </span>
                    </div>
                    <input type="number" name="budget" value={campaign.budget || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6524" />
                  </div>
                </FormField> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6525">
                  <FormField label="Recurring Period" required data-id="element-6526">
                    <SearchableSelect options={RECURRING_PERIODS} value={campaign.recurringBudgetPeriod ? campaign.recurringBudgetPeriod.charAt(0).toUpperCase() + campaign.recurringBudgetPeriod.slice(1) : ''} onChange={val => setCampaign({
                  ...campaign,
                  recurringBudgetPeriod: val.toLowerCase()
                })} placeholder="-- Select One --" data-id="element-6527" />
                  </FormField>

                  <FormField label="Recurring Budget Amount" required data-id="element-6528">
                    <div className="relative" data-id="element-6529">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6530">
                        <span className="text-gray-500 font-medium text-sm" data-id="element-6531">
                          THB
                        </span>
                      </div>
                      <input type="number" name="recurringBudget" value={campaign.recurringBudget || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6532" />
                    </div>
                  </FormField>
                </div>}
            </div>

            <div className="flex items-center pt-2" data-id="element-6533">
              <label className="flex items-center cursor-pointer" data-id="element-6534">
                <div className="relative" data-id="element-6535">
                  <input type="checkbox" name="strictlyEnforceBudget" className="sr-only" checked={campaign.strictlyEnforceBudget} onChange={e => setCampaign({
                  ...campaign,
                  strictlyEnforceBudget: e.target.checked
                })} data-id="element-6536" />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${campaign.strictlyEnforceBudget ? 'bg-[#F7941D]' : 'bg-gray-200'}`} data-id="element-6537" />
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${campaign.strictlyEnforceBudget ? 'transform translate-x-4' : ''}`} data-id="element-6538" />
                </div>
                <span className="text-sm text-gray-700 ml-3 font-medium" data-id="element-6539">
                  Strictly enforce budget
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Section 6: Campaign Constraints */}
        <div className="p-6 border-b border-gray-100" data-id="element-6540">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6541">
            Campaign Constraints
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-id="element-6542">
            <FormField label="Start Date" required data-id="element-6543">
              <div className="relative" data-id="element-6544">
                <input type="text" name="startDate" placeholder="DD/MM/YYYY" value={campaign.startDate || ''} onChange={handleInputChange} required className={inputClasses} data-id="element-6545" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-6546">
                  <CalendarIcon size={18} className="text-gray-400" data-id="element-6547" />
                </div>
              </div>
            </FormField>

            <FormField label="Expiry Date" required data-id="element-6548">
              <div className="relative" data-id="element-6549">
                <input type="text" name="expiryDate" placeholder="DD/MM/YYYY" value={campaign.expiryDate} onChange={handleInputChange} required className={inputClasses} data-id="element-6550" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-6551">
                  <CalendarIcon size={18} className="text-gray-400" data-id="element-6552" />
                </div>
              </div>
            </FormField>

            <FormField label="Min Discount" data-id="element-6553">
              <div className="relative" data-id="element-6554">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6555">
                  <span className="text-gray-500 font-medium text-sm" data-id="element-6556">THB</span>
                </div>
                <input type="number" name="minDiscount" value={campaign.minDiscount || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6557" />
              </div>
            </FormField>

            <FormField label="Max Discount" data-id="element-6558">
              <div className="relative" data-id="element-6559">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6560">
                  <span className="text-gray-500 font-medium text-sm" data-id="element-6561">THB</span>
                </div>
                <input type="number" name="maxDiscount" value={campaign.maxDiscount || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6562" />
              </div>
            </FormField>

            <FormField label="Min Transaction Value" data-id="element-6563">
              <div className="relative" data-id="element-6564">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6565">
                  <span className="text-gray-500 font-medium text-sm" data-id="element-6566">THB</span>
                </div>
                <input type="number" name="minTransactionValue" value={campaign.minTransactionValue || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6567" />
              </div>
            </FormField>

            <FormField label="Max Transaction Value" data-id="element-6568">
              <div className="relative" data-id="element-6569">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6570">
                  <span className="text-gray-500 font-medium text-sm" data-id="element-6571">THB</span>
                </div>
                <input type="number" name="maxTransactionValue" value={campaign.maxTransactionValue || ''} onChange={handleInputChange} min="0" placeholder="0.00" className={amountInputClasses} data-id="element-6572" />
              </div>
            </FormField>
          </div>
        </div>

        {/* Section 7: Targeting */}
        <div className="p-6 border-b border-gray-100" data-id="element-6573">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6574">
            Targeting
          </h2>
          <div className="space-y-6" data-id="element-6575">
            <FormField label="Limit usage to specific customers (localCustomerId)" tooltip="Enter a comma-separated list of customer IDs" data-id="element-6576">
              <textarea name="limitUsage" value={campaign.limitUsage || ''} onChange={handleInputChange} rows={3} placeholder="e.g. CUST001, CUST002, CUST003, CUST004" className={`${inputClasses} resize-none`} data-id="element-6577" />
              <p className="text-xs text-gray-400 mt-1" data-id="element-6578">
                Comma-separated list of customer IDs. Leave empty for no
                restriction.
              </p>
            </FormField>

            {selectedPaymentMethods['Card'] && <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-6" data-id="element-6579">
                <div className="flex justify-between items-center mb-4" data-id="element-6580">
                  <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wider" data-id="element-6581">
                    Card Targeting Rules
                  </h3>
                  {activeCardRules.length === 0 && <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-6582">
                      No rules — all cards accepted
                    </span>}
                </div>

                {activeCardRules.length === 0 && <div className="text-center py-6 border border-dashed border-blue-200 rounded-lg bg-white/50 mb-4" data-id="element-6583">
                    <CreditCardIcon size={28} className="mx-auto text-blue-300 mb-2" data-id="element-6584" />
                    <p className="text-sm text-gray-500 mb-1" data-id="element-6585">
                      No card targeting rules configured
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-6586">
                      Add rules to restrict which cards are eligible for this
                      campaign
                    </p>
                  </div>}

                {/* Active Rules */}
                <div className="space-y-3" data-id="element-6587">
                  {activeCardRules.map((ruleId, index) => {
                const ruleDef = CARD_RULE_DEFINITIONS.find(r => r.id === ruleId);
                if (!ruleDef) return null;
                const RuleIcon = ruleDef.icon;
                return <div key={ruleId} className="bg-white border border-gray-200 rounded-lg" data-id="element-6588">
                        {/* Rule Header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100" data-id="element-6589">
                          <div className="flex items-center gap-2" data-id="element-6590">
                            <span className="text-xs font-bold text-gray-400 bg-gray-200 w-5 h-5 rounded flex items-center justify-center" data-id="element-6591">
                              {index + 1}
                            </span>
                            <RuleIcon size={16} className="text-blue-600" data-id="element-6592" />
                            <span className="text-sm font-semibold text-gray-900" data-id="element-6593">
                              {ruleDef.label}
                            </span>
                          </div>
                          <button type="button" onClick={() => removeCardRule(ruleId)} className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50" title="Remove rule" data-id="element-6594">
                            <XIcon size={14} data-id="element-6595" />
                          </button>
                        </div>

                        {/* Rule Content */}
                        <div className="p-4" data-id="element-6596">
                          {ruleId === 'brands' && <div className="flex flex-wrap gap-2" data-id="element-6597">
                              {CARD_BRANDS.map(brand => <label key={brand} className={`flex items-center px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${selectedCardBrands[brand] ? 'bg-orange-50 border-[#F7941D] text-[#92400E] font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-6598">
                                  <input type="checkbox" className="sr-only" checked={selectedCardBrands[brand] || false} onChange={() => handleCardBrandToggle(brand)} data-id="element-6599" />
                                  {brand}
                                </label>)}
                            </div>}

                          {ruleId === 'types' && <div className="flex flex-wrap gap-2" data-id="element-6600">
                              {CARD_TYPES.map(type => <label key={type} className={`flex items-center px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${selectedCardTypes[type] ? 'bg-orange-50 border-[#F7941D] text-[#92400E] font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-6601">
                                  <input type="checkbox" className="sr-only" checked={selectedCardTypes[type] || false} onChange={() => handleCardTypeToggle(type)} data-id="element-6602" />
                                  {type}
                                </label>)}
                            </div>}

                          {ruleId === 'bins' && <div data-id="element-6603">
                              <input type="text" name="cardBin" value={campaign.cardBin || ''} onChange={handleInputChange} placeholder="e.g. 411111, 422222, 553344" className={inputClasses} data-id="element-6604" />
                              <p className="text-xs text-gray-400 mt-1" data-id="element-6605">
                                Comma-separated list of 6 or 8 digit BINs
                              </p>
                            </div>}

                          {ruleId === 'countries' && <CountryMultiSelect selectedCountries={selectedCountries} onToggle={handleCountryToggle} countrySearch={countrySearch} onSearchChange={setCountrySearch} filteredCountries={filteredCountries} selectedCountryList={selectedCountryList} data-id="element-6606" />}

                          {ruleId === 'categories' && <div className="flex flex-wrap gap-2" data-id="element-6607">
                              {CARD_CATEGORIES.map(cat => <label key={cat} className={`flex items-center px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${selectedCardCategories[cat] ? 'bg-orange-50 border-[#F7941D] text-[#92400E] font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-6608">
                                  <input type="checkbox" className="sr-only" checked={selectedCardCategories[cat] || false} onChange={() => handleCardCategoryToggle(cat)} data-id="element-6609" />
                                  {cat}
                                </label>)}
                            </div>}
                        </div>
                      </div>;
              })}
                </div>

                {/* Add Rule Button */}
                {availableRules.length > 0 && <div className="relative mt-4" data-id="element-6610">
                    <button type="button" onClick={() => setShowAddRuleMenu(!showAddRuleMenu)} className="flex items-center gap-2 px-4 py-2 border border-dashed border-blue-300 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors w-full justify-center" data-id="element-6611">
                      <PlusIcon size={16} data-id="element-6612" />
                      Add Card Rule
                      <ChevronDownIcon size={14} className={`transition-transform ${showAddRuleMenu ? 'rotate-180' : ''}`} data-id="element-6613" />
                    </button>

                    {showAddRuleMenu && <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowAddRuleMenu(false)} data-id="element-6614" />
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-6615">
                          {availableRules.map(rule => {
                    const RIcon = rule.icon;
                    return <button key={rule.id} type="button" onClick={() => addCardRule(rule.id)} className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0" data-id="element-6616">
                                <RIcon size={16} className="text-blue-500 flex-shrink-0" data-id="element-6617" />
                                <div data-id="element-6618">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-6619">
                                    {rule.label}
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-6620">
                                    {rule.description}
                                  </div>
                                </div>
                              </button>;
                  })}
                        </div>
                      </>}
                  </div>}

                {activeCardRules.length > 0 && availableRules.length === 0 && <p className="text-xs text-blue-500 mt-3 text-center" data-id="element-6621">
                    All available rule types have been added
                  </p>}
              </div>}

            <FormField label="Valid on which days" data-id="element-6622">
              <p className="text-xs text-gray-500 mb-3" data-id="element-6623">
                Nothing selected means valid on all days
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-6624">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => <label key={day} className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${selectedDays[day] ? 'bg-orange-50 border-[#F7941D] text-[#92400E]' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-6625">
                    <input type="checkbox" className="sr-only" checked={selectedDays[day] || false} onChange={() => handleDayToggle(day)} data-id="element-6626" />
                    <span className="text-sm font-medium" data-id="element-6627">{day}</span>
                  </label>)}
              </div>
            </FormField>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-gray-50 flex justify-end gap-3" data-id="element-6628">
          <button type="button" onClick={navigateBack} className="px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors" data-id="element-6629">
            Cancel
          </button>
          <button type="submit" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6630">
            {isEditing ? 'Save Changes' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>;
}